import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import {
  type ChangeEvent,
  type FC,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { type DiffLine, computeDiff } from "./computeDiff";
import { type SideBySideRow, buildSideBySideRows } from "./diffSideBySide";
import { MODULE_NESTED_TAG_DIFF_OVERLAY_Z_INDEX } from "./moduleModalStacking";

/** Per-module JS diff for 配信条件連動 (gated) modules — Tab 2. */
export type LinkedModuleDiffItem = {
  id?: string;
  name: string;
  /** Published / baseline code (left). */
  left: string;
  /** Draft / pending code (right). */
  right: string;
};

export type TagDiffModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Renders above the diff (e.g. publish form + change summary). */
  prependBody?: ReactNode;
  /** Shown above the left column (e.g. baseline / older version). */
  leftColumnLabel: string;
  /** Shown above the right column (e.g. draft / newer version). */
  rightColumnLabel: string;
  /** 説明（任意） for the left (baseline) side — shown in that column’s header. */
  leftColumnDescription?: string | null;
  /** 説明（任意） for the right side — shown in that column’s header. */
  rightColumnDescription?: string | null;
  /**
   * When set, the right column’s 説明 is a controlled textarea (e.g. save-draft confirm).
   * `rightColumnDescription` is the value.
   */
  onRightColumnDescriptionChange?: (value: string) => void;
  /** Hint under 「説明（任意）」 on the right when it is editable. */
  rightColumnDescriptionHelp?: ReactNode;
  diffLines: DiffLine[] | null;
  isLoadingDiff?: boolean;
  warningText?: string | null;
  mode: "confirm" | "info";
  onConfirm?: () => void;
  confirmLabel?: string;
  confirmColorScheme?: string;
  isConfirming?: boolean;
  /** When true, the primary confirm action is disabled (e.g. missing version name). */
  confirmDisabled?: boolean;
  /**
   * `fullscreen`: GTM-style publish — 100vw×100vh, scroll lock, z-index above app chrome
   * (notification badge, headers, floating controls). `default`: existing centered modal.
   */
  layout?: "default" | "fullscreen";
  /**
   * When true, raise z-index above `ModuleEditorModal` so e.g. 差分プレビュー is not
   * hidden behind the editor (see `moduleModalStacking` MODULE_MODAL_*).
   */
  stackOnModuleEditor?: boolean;
  /**
   * When provided (including `[]`), show two tabs: SDK / コンパイル + 配信条件連動.
   * Omit / `null` to keep the single SDK merge diff (no tabs).
   */
  linkedModuleDiffs?: LinkedModuleDiffItem[] | null;
  /** Left column label for linked-module diffs (default: 公開中). */
  linkedLeftColumnLabel?: string;
  /** Right column label for linked-module diffs (default: 下書き). */
  linkedRightColumnLabel?: string;
  /** Help text above linked-module cards (omit to use publish default). */
  linkedModulesHelpText?: string | null;
};

/**
 * Above `NotificationWithBadgeIcon` (`zIndex={9999}`). Must apply z-index on
 * `ModalContent`’s outer container (`containerProps`), not only the inner dialog:
 * `ModalOverlay` is a sibling of that container; z-index on the inner section
 * does not stack above the overlay, which then covers the whole screen.
 */
const TAG_DIFF_MODAL_Z_INDEX = 10000;

/** Full-screen publish: above notification (9999), app bars, and other high z-index UI. */
const TAG_DIFF_MODAL_FULLSCREEN_Z_INDEX = 100_000;

/** Light “code editor” chrome: gutter + plain white pane, VS-like token colors. */
const EDITOR_BORDER_COLOR = "#E0E0E0";
const GUTTER_BG = "#F3F3F3";
const GUTTER_TEXT = "#A8A8A8";
/** Narrow strip between left/right code; matches header spacer width. */
const BETWEEN_COLUMNS_W = "2.25rem";
const MONO =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const prismTokenSx = {
  color: "#1a1a1a",
  "& .token.keyword, & .token.boolean": { color: "#81209A" },
  "& .token.string, & .token.char, & .token.template-string .token.string": {
    color: "#A31515",
  },
  "& .token.comment": { color: "#008000" },
  "& .token.function, & .token.class-name": { color: "#000000" },
  "& .token.number": { color: "#098658" },
  "& .token.operator, & .token.punctuation": { color: "#000000" },
  "& .token.regex": { color: "#811B9C" },
};

type CodeAreaStyle = { bg: string; placeholder: boolean };

const codeAreaStyle = (
  side: "left" | "right",
  rowKind: SideBySideRow["rowKind"]
): CodeAreaStyle => {
  if (rowKind === "both") {
    return { bg: "#FFFFFF", placeholder: false };
  }
  if (rowKind === "leftOnly") {
    if (side === "left") {
      return { bg: "#FFEBEE", placeholder: false };
    }
    return { bg: "#FAFAFA", placeholder: true };
  }
  if (side === "right") {
    return { bg: "#E8F5E9", placeholder: false };
  }
  return { bg: "#FAFAFA", placeholder: true };
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function highlightJavaScript(line: string): string {
  const lang = Prism.languages.javascript;
  if (!lang) {
    return escapeHtml(line);
  }
  return Prism.highlight(line, lang, "javascript");
}

type DiffColumnHeaderProps = {
  columnLabel: string;
  description: string | null;
  onDescriptionChange?: (value: string) => void;
  descriptionHelp?: ReactNode;
  /** When false, only the column label is shown (no 説明 block). */
  showDescription?: boolean;
};

const DiffColumnHeader: FC<DiffColumnHeaderProps> = ({
  columnLabel,
  description,
  onDescriptionChange,
  descriptionHelp,
  showDescription = true,
}) => {
  const displayValue = description ?? "";
  const hasText = displayValue.trim().length > 0;
  const isEditable = onDescriptionChange != null;

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onDescriptionChange?.(e.target.value);
    },
    [onDescriptionChange]
  );

  return (
    <Box
      flex={1}
      minW={0}
      bg="gray.100"
      px={3}
      py={2}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={EDITOR_BORDER_COLOR}
      borderRadius="sm"
    >
      <VStack align="stretch" spacing={2}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="gray.700"
          wordBreak="break-word"
        >
          {columnLabel}
        </Text>
        {showDescription && (
          <Box>
            {isEditable && descriptionHelp != null && (
              <Box mt={1} fontSize="xs" color="gray.600">
                {descriptionHelp}
              </Box>
            )}
            {isEditable ? (
              <Textarea
                value={displayValue}
                onChange={handleDescriptionChange}
                mt={1}
                size="sm"
                rows={2}
                resize="vertical"
                maxH="120px"
                fontSize="xs"
              />
            ) : (
              <Text
                mt={1}
                fontSize="xs"
                color={hasText ? "gray.800" : "gray.500"}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {hasText ? displayValue : "（なし）"}
              </Text>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

function renderSideLine(
  side: "left" | "right",
  row: SideBySideRow,
  lineNo: number
): ReactElement {
  const raw = side === "left" ? row.left : row.right;
  const { bg, placeholder } = codeAreaStyle(side, row.rowKind);
  const hasCode = raw != null && raw.length > 0;

  return (
    <Flex w="100%" align="stretch" minH="1.5rem">
      <Box
        flexShrink={0}
        w="3rem"
        bg={GUTTER_BG}
        borderRightWidth="1px"
        borderRightStyle="solid"
        borderRightColor={EDITOR_BORDER_COLOR}
        py="3px"
        pr={2}
        fontSize="11px"
        lineHeight="1.45"
        fontFamily={MONO}
        color={GUTTER_TEXT}
        textAlign="right"
        userSelect="none"
      >
        {lineNo}
      </Box>
      <Box
        flex={1}
        minW={0}
        bg={bg}
        py="3px"
        pl={2}
        pr={2}
        fontSize="12px"
        lineHeight="1.45"
        fontFamily={MONO}
        whiteSpace="pre-wrap"
        wordBreak="break-all"
        color={placeholder ? "gray.500" : undefined}
        sx={!placeholder && hasCode ? prismTokenSx : undefined}
        {...(placeholder || !hasCode
          ? { children: "\u00a0" }
          : {
              dangerouslySetInnerHTML: {
                __html: highlightJavaScript(raw),
              },
            })}
      />
    </Flex>
  );
}

type DiffSidePaneProps = {
  rows: SideBySideRow[];
  side: "left" | "right";
  scrollRef: RefObject<HTMLDivElement>;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
};

const DiffSidePane: FC<DiffSidePaneProps> = ({
  rows,
  side,
  scrollRef,
  onScroll,
}) => {
  return (
    <Box
      ref={scrollRef}
      flex={1}
      minW={0}
      overflow="auto"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={EDITOR_BORDER_COLOR}
      borderRadius="sm"
      bg="#FFFFFF"
      onScroll={onScroll}
    >
      {rows.map((row, idx) => {
        const lineNo = idx + 1;
        return (
          <Box key={`${side}-${idx}`} w="100%">
            {renderSideLine(side, row, lineNo)}
          </Box>
        );
      })}
    </Box>
  );
};

const DiffArrowGutter: FC = () => (
  <Flex
    flexShrink={0}
    w={BETWEEN_COLUMNS_W}
    alignItems="center"
    justifyContent="center"
    alignSelf="stretch"
    aria-hidden
  >
    <Text
      as="span"
      fontSize="14px"
      lineHeight={1}
      color="gray.600"
      fontWeight="bold"
      userSelect="none"
    >
      →
    </Text>
  </Flex>
);

type SideBySideDiffViewProps = {
  diffLines: DiffLine[] | null;
  isLoadingDiff?: boolean;
  leftColumnLabel: string;
  rightColumnLabel: string;
  leftColumnDescription?: string | null;
  rightColumnDescription?: string | null;
  onRightColumnDescriptionChange?: (value: string) => void;
  rightColumnDescriptionHelp?: ReactNode;
  /** Hide 説明 under column labels (used for linked-module cards). */
  showColumnDescriptions?: boolean;
  /**
   * When set, panes use a fixed max height instead of filling remaining modal
   * space (linked-module accordion cards).
   */
  panesMaxHeight?: string | number;
};

const SideBySideDiffView: FC<SideBySideDiffViewProps> = ({
  diffLines,
  isLoadingDiff = false,
  leftColumnLabel,
  rightColumnLabel,
  leftColumnDescription = null,
  rightColumnDescription = null,
  onRightColumnDescriptionChange,
  rightColumnDescriptionHelp,
  showColumnDescriptions = true,
  panesMaxHeight,
}) => {
  const rows = useMemo(
    () => (diffLines === null ? [] : buildSideBySideRows(diffLines)),
    [diffLines]
  );

  const leftScrollRef = useRef<HTMLDivElement | null>(null);
  const rightScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);

  const handleLeftScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    if (isSyncingScrollRef.current) return;
    const right = rightScrollRef.current;
    if (!right) return;
    const nextTop = e.currentTarget.scrollTop;
    if (Math.abs(right.scrollTop - nextTop) < 0.5) return;
    isSyncingScrollRef.current = true;
    right.scrollTop = nextTop;
    queueMicrotask(() => {
      isSyncingScrollRef.current = false;
    });
  }, []);

  const handleRightScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    if (isSyncingScrollRef.current) return;
    const left = leftScrollRef.current;
    if (!left) return;
    const nextTop = e.currentTarget.scrollTop;
    if (Math.abs(left.scrollTop - nextTop) < 0.5) return;
    isSyncingScrollRef.current = true;
    left.scrollTop = nextTop;
    queueMicrotask(() => {
      isSyncingScrollRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (rows.length === 0) return;
    const left = leftScrollRef.current;
    const right = rightScrollRef.current;
    if (!left || !right) return;
    isSyncingScrollRef.current = true;
    left.scrollTop = 0;
    right.scrollTop = 0;
    queueMicrotask(() => {
      isSyncingScrollRef.current = false;
    });
  }, [diffLines, rows.length]);

  if (isLoadingDiff) {
    return (
      <Text fontSize="sm" color="gray.500" px={{ base: 2, md: 3 }} py={4}>
        差分を読み込み中...
      </Text>
    );
  }

  if (diffLines === null) {
    return (
      <Text fontSize="sm" color="gray.400" px={{ base: 2, md: 3 }} py={4}>
        差分を取得できませんでした。
      </Text>
    );
  }

  if (diffLines.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" px={{ base: 2, md: 3 }} py={4}>
        差分はありません（内容が同一です）。
      </Text>
    );
  }

  const fillRemaining = panesMaxHeight == null;

  return (
    <Flex
      flex={fillRemaining ? 1 : undefined}
      minH={fillRemaining ? 0 : undefined}
      direction="column"
      overflow="hidden"
      px={{ base: 2, md: 3 }}
      py={3}
      gap={3}
    >
      <HStack align="stretch" spacing={3} flexShrink={0}>
        <DiffColumnHeader
          columnLabel={leftColumnLabel}
          description={leftColumnDescription}
          showDescription={showColumnDescriptions}
        />
        <Box
          w={BETWEEN_COLUMNS_W}
          flexShrink={0}
          alignSelf="stretch"
          aria-hidden
        />
        <DiffColumnHeader
          columnLabel={rightColumnLabel}
          description={rightColumnDescription}
          onDescriptionChange={onRightColumnDescriptionChange}
          descriptionHelp={rightColumnDescriptionHelp}
          showDescription={showColumnDescriptions}
        />
      </HStack>
      <HStack
        align="stretch"
        spacing={3}
        flex={fillRemaining ? 1 : undefined}
        minH={fillRemaining ? 0 : undefined}
        maxH={panesMaxHeight}
        h={panesMaxHeight}
        overflow="hidden"
      >
        <DiffSidePane
          rows={rows}
          side="left"
          scrollRef={leftScrollRef}
          onScroll={handleLeftScroll}
        />
        <DiffArrowGutter />
        <DiffSidePane
          rows={rows}
          side="right"
          scrollRef={rightScrollRef}
          onScroll={handleRightScroll}
        />
      </HStack>
    </Flex>
  );
};

type LinkedModuleDiffCardProps = {
  item: LinkedModuleDiffItem;
  leftColumnLabel: string;
  rightColumnLabel: string;
};

const LinkedModuleDiffCard: FC<LinkedModuleDiffCardProps> = ({
  item,
  leftColumnLabel,
  rightColumnLabel,
}) => {
  const diffLines = useMemo(
    () => computeDiff(item.left, item.right),
    [item.left, item.right]
  );

  return (
    <SideBySideDiffView
      diffLines={diffLines}
      leftColumnLabel={leftColumnLabel}
      rightColumnLabel={rightColumnLabel}
      showColumnDescriptions={false}
      panesMaxHeight="min(40vh, 360px)"
    />
  );
};

type LinkedModulesDiffPanelProps = {
  items: LinkedModuleDiffItem[];
  leftColumnLabel: string;
  rightColumnLabel: string;
  helpText?: string | null;
};

const DEFAULT_LINKED_HELP =
  "配信条件連動モジュールはCDNマージに含まれません。公開中のコードと下書きの差分をモジュールごとに確認できます。";

const LinkedModulesDiffPanel: FC<LinkedModulesDiffPanelProps> = ({
  items,
  leftColumnLabel,
  rightColumnLabel,
  helpText = DEFAULT_LINKED_HELP,
}) => {
  if (items.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" px={{ base: 2, md: 3 }} py={4}>
        変更のある配信条件連動モジュールはありません。
      </Text>
    );
  }

  return (
    <Box flex={1} minH={0} overflowY="auto" px={{ base: 2, md: 3 }} py={3}>
      {helpText != null && helpText !== "" && (
        <Text fontSize="xs" color="gray.500" mb={3}>
          {helpText}
        </Text>
      )}
      <Accordion allowMultiple defaultIndex={[0]}>
        {items.map((item) => (
          <AccordionItem
            key={item.id ?? item.name}
            borderWidth="1px"
            borderStyle="solid"
            borderColor="gray.200"
            borderRadius="md"
            mb={2}
            bg="white"
            overflow="hidden"
            _last={{ mb: 0 }}
          >
            <AccordionButton
              px={4}
              py={3}
              _hover={{ bg: "gray.50" }}
              _focus={{ boxShadow: "none" }}
            >
              <HStack spacing={2} flex={1} textAlign="left" minW={0}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.800"
                  wordBreak="break-word"
                >
                  {item.name}
                </Text>
                <Badge colorScheme="purple" size="sm" flexShrink={0}>
                  配信条件連動
                </Badge>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={3} pt={0} borderTopWidth="1px">
              <LinkedModuleDiffCard
                item={item}
                leftColumnLabel={leftColumnLabel}
                rightColumnLabel={rightColumnLabel}
              />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export const TagDiffModal: FC<TagDiffModalProps> = ({
  isOpen,
  onClose,
  title,
  leftColumnLabel,
  rightColumnLabel,
  leftColumnDescription = null,
  rightColumnDescription = null,
  onRightColumnDescriptionChange,
  rightColumnDescriptionHelp,
  diffLines,
  isLoadingDiff = false,
  warningText,
  mode,
  onConfirm,
  confirmLabel = "確定",
  confirmColorScheme = "blue",
  isConfirming = false,
  prependBody,
  confirmDisabled = false,
  layout = "default",
  stackOnModuleEditor = false,
  linkedModuleDiffs = null,
  linkedLeftColumnLabel = "公開中",
  linkedRightColumnLabel = "下書き",
  linkedModulesHelpText,
}) => {
  const isFullscreen = layout === "fullscreen";
  const showDiffTabs = linkedModuleDiffs != null;
  const linkedCount = linkedModuleDiffs?.length ?? 0;
  const [diffTabIndex, setDiffTabIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setDiffTabIndex(0);
    }
  }, [isOpen]);

  const zStack = (() => {
    if (stackOnModuleEditor && !isFullscreen) {
      return MODULE_NESTED_TAG_DIFF_OVERLAY_Z_INDEX;
    }
    if (isFullscreen) {
      return TAG_DIFF_MODAL_FULLSCREEN_Z_INDEX;
    }
    return TAG_DIFF_MODAL_Z_INDEX;
  })();

  const handleDiffTabChange = useCallback((index: number) => {
    setDiffTabIndex(index);
  }, []);

  const sdkDiffView = (
    <SideBySideDiffView
      diffLines={diffLines}
      isLoadingDiff={isLoadingDiff}
      leftColumnLabel={leftColumnLabel}
      rightColumnLabel={rightColumnLabel}
      leftColumnDescription={leftColumnDescription}
      rightColumnDescription={rightColumnDescription}
      onRightColumnDescriptionChange={onRightColumnDescriptionChange}
      rightColumnDescriptionHelp={rightColumnDescriptionHelp}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={!isFullscreen}
      scrollBehavior="inside"
      blockScrollOnMount={isFullscreen}
      closeOnEsc
      motionPreset={isFullscreen ? "none" : "slideInBottom"}
    >
      <ModalOverlay
        zIndex={zStack}
        bg={isFullscreen ? "blackAlpha.600" : undefined}
      />
      <ModalContent
        containerProps={{ zIndex: zStack + 1 }}
        {...(isFullscreen
          ? {
              position: "fixed",
              inset: 0,
              m: 0,
              maxW: "100vw",
              w: "100vw",
              h: "100vh",
              maxH: "100vh",
              minH: "100vh",
              borderRadius: "none",
            }
          : {
              w: "90vw",
              maxW: "90vw",
              h: "90vh",
              maxH: "90vh",
              m: 0,
              borderRadius: "md",
            })}
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          display="flex"
          flexDirection={{ base: "column", sm: "row" }}
          alignItems={{ base: "stretch", sm: "flex-start" }}
          flexShrink={0}
          borderBottomWidth="1px"
          py={3}
          px={{ base: 2, md: 3 }}
          gap={3}
        >
          <Box flex={1} minW={0}>
            <Text fontWeight="bold" fontSize="md" wordBreak="break-word">
              {title}
            </Text>
            {warningText && (
              <Text mt={2} fontSize="sm" color="orange.600">
                {warningText}
              </Text>
            )}
          </Box>
          <HStack
            spacing={2}
            flexShrink={0}
            alignSelf={{ base: "flex-end", sm: "flex-start" }}
            flexWrap="wrap"
            justify={{ base: "flex-end", sm: "flex-start" }}
          >
            {mode === "confirm" && (
              <>
                <Button variant="ghost" onClick={onClose}>
                  キャンセル
                </Button>
                <Button
                  colorScheme={confirmColorScheme}
                  isLoading={isConfirming}
                  onClick={onConfirm}
                  isDisabled={confirmDisabled}
                >
                  {confirmLabel}
                </Button>
              </>
            )}
            {mode === "info" && (
              <Button onClick={onClose} colorScheme="blue" variant="solid">
                閉じる
              </Button>
            )}
          </HStack>
        </ModalHeader>

        <ModalBody
          flex={1}
          display="flex"
          flexDirection="column"
          minH={0}
          overflow="hidden"
          p={0}
        >
          {prependBody != null && (
            <Box
              flexShrink={0}
              px={{ base: 2, md: 3 }}
              py={3}
              borderBottomWidth="1px"
              borderColor="gray.200"
              overflowY="auto"
              maxH={isFullscreen ? "min(50vh, 560px)" : "40vh"}
            >
              {prependBody}
            </Box>
          )}

          {showDiffTabs ? (
            <Tabs
              index={diffTabIndex}
              onChange={handleDiffTabChange}
              display="flex"
              flexDirection="column"
              flex={1}
              minH={0}
              overflow="hidden"
              isLazy
            >
              <TabList
                flexShrink={0}
                px={{ base: 2, md: 3 }}
                borderBottomWidth="1px"
              >
                <Tab fontSize="sm">SDK / コンパイル</Tab>
                <Tab fontSize="sm">配信条件連動 ({linkedCount})</Tab>
              </TabList>
              <TabPanels
                display="flex"
                flexDirection="column"
                flex={1}
                minH={0}
                overflow="hidden"
              >
                <TabPanel
                  p={0}
                  display="flex"
                  flexDirection="column"
                  flex={1}
                  minH={0}
                  overflow="hidden"
                  h="100%"
                >
                  {sdkDiffView}
                </TabPanel>
                <TabPanel
                  p={0}
                  display="flex"
                  flexDirection="column"
                  flex={1}
                  minH={0}
                  overflow="hidden"
                  h="100%"
                >
                  <LinkedModulesDiffPanel
                    items={linkedModuleDiffs ?? []}
                    leftColumnLabel={linkedLeftColumnLabel}
                    rightColumnLabel={linkedRightColumnLabel}
                    helpText={
                      linkedModulesHelpText === undefined
                        ? DEFAULT_LINKED_HELP
                        : linkedModulesHelpText
                    }
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            sdkDiffView
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
